import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import 'react-lazy-load-image-component/src/effects/blur.css'
import { 
  ExternalLink, 
  Globe, 
  Share2, 
  Copy, 
  ArrowLeft,
  Calendar,
  Hash,
  Eye,
  Chrome,
  CheckCircle,
  ArrowRight,
  AlertCircle,
  X
} from 'lucide-react'
import chronoLogo from '../images/Chrono Session - removed bg logo.png'
import ReadyToOrganizeCTA from '../components/ui/ReadyToOrganizeCTA'

interface Tab {
  id: string
  url: string
  title: string
  favicon?: string
}

interface Session {
  id: string
  name: string
  summary?: string
  thumbnail_url?: string
  tabs: Tab[]
  created_at: string
  updated_at: string
}

const SharePage = () => {
  const { sessionId } = useParams<{ sessionId: string }>()
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [showAllTabs, setShowAllTabs] = useState(false)
  const [showResultsModal, setShowResultsModal] = useState(false)
  const [tabResults, setTabResults] = useState<{
    openedCount: number
    blockedCount: number
    blockedUrls: string[]
    totalTabs: number
  } | null>(null)
  const [showFaq, setShowFaq] = useState(false)

  // Calculate which tabs to show
  const tabsToShow = showAllTabs ? session?.tabs : session?.tabs?.slice(0, 10)
  const remainingCount = session?.tabs ? Math.max(0, session.tabs.length - 10) : 0

  useEffect(() => {
    const fetchSession = async () => {
      try {
        setLoading(true)
        
        // Fetch session from server API
        const getApiBaseUrl = () => {
          return import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || 'https://nutty-alicia-chrono-session-server-e76d814f.koyeb.app';
        };

        const serverUrl = getApiBaseUrl();
          
        const response = await fetch(`${serverUrl}/api/sessions/shared/${sessionId}`)
        
        if (!response.ok) {
          if (response.status === 404) {
            setError('Session not found')
          } else {
            setError('Failed to load session')
          }
          return
        }
        
        const data = await response.json()
        
        if (data.success && data.session) {
          // Transform server data to match our interface
          const sessionData: Session = {
            id: data.session.id,
            name: data.session.name,
            summary: data.session.summary,
            thumbnail_url: data.session.thumbnail_url,
            tabs: data.session.tabs.map((tab: any, index: number) => ({
              id: tab.id || String(index + 1),
              url: tab.url,
              title: tab.title,
              favicon: tab.favicon
            })),
            created_at: data.session.created_at,
            updated_at: data.session.created_at // Use created_at as fallback
          }
          
          setSession(sessionData)
          console.log(`✅ Loaded shared session: "${sessionData.name}" with ${sessionData.tabs.length} tabs`)
        } else {
          setError('Invalid session data')
        }
      } catch (err) {
        setError('Failed to load session')
        console.error('Error fetching session:', err)
      } finally {
        setLoading(false)
      }
    }

    if (sessionId) {
      fetchSession()
    }
  }, [sessionId])

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy link:', err)
    }
  }

  const handleOpenAllTabs = () => {
    if (!session) return
    
    console.log('🔍 Opening all tabs:', {
      totalTabs: session.tabs.length,
      visibleTabs: tabsToShow?.length,
      showAllTabs: showAllTabs,
      remainingCount: remainingCount
    })
    
    // Open ALL tabs, not just the visible ones
    let openedCount = 0
    let blockedCount = 0
    const blockedUrls: string[] = []
    
    session.tabs.forEach((tab, index) => {
      console.log(`📂 Opening tab ${index + 1}/${session.tabs.length}:`, tab.title)
      setTimeout(() => {
        try {
          // Skip certain URLs that browsers typically block
          const url = tab.url.toLowerCase()
          const isBlockedUrl = url.includes('chrome://') || 
                              url.includes('chrome-extension://') || 
                              url.includes('about:') ||
                              url.includes('new tab') ||
                              url.includes('extensions')
          
          if (isBlockedUrl) {
            blockedCount++
            blockedUrls.push(tab.title)
            console.warn(`🚫 Skipping blocked URL: ${tab.title} (${tab.url})`)
            return
          }
          
          const newWindow = window.open(tab.url, '_blank')
          if (newWindow) {
            openedCount++
            console.log(`✅ Successfully opened tab ${index + 1}: ${tab.title}`)
          } else {
            blockedCount++
            blockedUrls.push(tab.title)
            console.warn(`⚠️ Popup blocked for tab ${index + 1}: ${tab.title}`)
          }
        } catch (error) {
          blockedCount++
          blockedUrls.push(tab.title)
          console.error(`❌ Failed to open tab ${index + 1}:`, error)
        }
      }, index * 100) // Stagger opening to avoid popup blocking
    })
    
    // Show user feedback
    setTimeout(() => {
      if (openedCount > 0) {
        console.log(`🎉 Successfully opened ${openedCount}/${session.tabs.length} tabs`)
        
        // Show results in modal
        setTabResults({
          openedCount,
          blockedCount,
          blockedUrls,
          totalTabs: session.tabs.length
        })
        setShowResultsModal(true)
      } else {
        console.warn('⚠️ No tabs opened - popup blocker may be active')
        setTabResults({
          openedCount: 0,
          blockedCount: session.tabs.length,
          blockedUrls: session.tabs.map(tab => tab.title),
          totalTabs: session.tabs.length
        })
        setShowResultsModal(true)
      }
    }, session.tabs.length * 100 + 500)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // Helper function to truncate URL with smart logic
  const truncateUrl = (url: string, maxLength: number) => {
    if (url.length <= maxLength) return url
    
    // Try to keep the domain visible
    try {
      const urlObj = new URL(url)
      const domain = urlObj.hostname
      const path = urlObj.pathname + urlObj.search
      
      if (domain.length + 10 < maxLength) {
        const remainingLength = maxLength - domain.length - 3 // 3 for "..."
        const truncatedPath = path.length > remainingLength ? path.substring(0, remainingLength) + '...' : path
        return domain + truncatedPath
      }
    } catch {
      // If URL parsing fails, just truncate normally
    }
    
    return url.substring(0, maxLength) + '...'
  }

  // Helper function to truncate title with smart logic
  const truncateTitle = (title: string, maxLength: number) => {
    if (title.length <= maxLength) return title
    return title.substring(0, maxLength) + '...'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <motion.div 
          className="text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Loading session...</h2>
          <p className="text-gray-600">Please wait while we fetch your shared session</p>
        </motion.div>
      </div>
    )
  }

  if (error || !session) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <motion.div 
          className="text-center max-w-md mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="w-20 h-20 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Hash className="w-10 h-10 text-red-500" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-3">Session Not Found</h1>
          <p className="text-lg text-gray-600 mb-8 leading-relaxed">
            The session you're looking for doesn't exist or has been removed.
          </p>
          <button 
            onClick={() => window.location.href = '/'}
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-full font-semibold hover:bg-blue-700 transition-all duration-300 shadow-lg"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Go Home
          </button>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Modern Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-lg border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16">
            {/* Logo */}
            <div className="flex items-center space-x-2 sm:space-x-3">
              <button 
                onClick={() => window.location.href = '/'}
                className="hidden sm:flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="font-medium text-sm sm:text-base">Home</span>
              </button>
              <div className="flex items-center space-x-2 sm:space-x-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center">
                  <img src={chronoLogo} alt="Chrono Session" className="w-8 h-8 sm:w-10 sm:h-10 object-contain" width="40" height="40" />
                </div>
                <div>
                  <span className="text-lg sm:text-xl font-bold text-gray-900">Chrono Session</span>
                  <div className="text-xs text-gray-500 -mt-1 hidden sm:block">Shared Session</div>
                </div>
              </div>
            </div>
            
            {/* Header Actions */}
            <div className="flex items-center space-x-2 sm:space-x-3">
              <button 
                onClick={handleCopyLink}
                className="hidden sm:flex items-center px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-all duration-200 text-sm"
              >
                {copied ? (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-2" />
                    Copy Link
                  </>
                )}
              </button>
              <button 
                onClick={() => window.open('https://chrome.google.com/webstore', '_blank')}
                className="inline-flex items-center px-3 py-2 sm:px-4 sm:py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-all duration-300 shadow-lg text-sm sm:text-base"
              >
                <Chrome className="w-4 h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Get Extension</span>
                <span className="sm:hidden">Get</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Session Hero Section */}
        <motion.div 
          className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 md:p-8 lg:p-12 mb-8"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="flex flex-col lg:flex-row items-start lg:items-center gap-6 lg:gap-8">
            
            {/* Session Info */}
            <div className="flex-1 space-y-4">
              <div className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                <Share2 className="w-4 h-4 mr-2" />
                Shared Session
              </div>
              
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
                {session.name}
              </h1>
              
                  {session.summary && (
                <p className="text-lg md:text-xl text-gray-600 leading-relaxed max-w-3xl">
                      {session.summary}
                </p>
              )}
              
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                <div className="flex items-center">
                  <Calendar className="w-4 h-4 mr-2" />
                  {formatDate(session.created_at)}
                </div>
                <div className="flex items-center">
                  <Globe className="w-4 h-4 mr-2" />
                  {session.tabs.length} tabs
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <button 
                  onClick={handleOpenAllTabs}
                  className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-full font-semibold hover:bg-blue-700 transition-all duration-300 shadow-lg"
                >
                  <ExternalLink className="w-5 h-5 mr-2" />
                  Open All Tabs ({session.tabs.length})
                </button>
                
                <button 
                  onClick={handleCopyLink}
                  className="inline-flex items-center px-6 py-3 border border-gray-300 text-gray-700 rounded-full font-semibold hover:border-blue-400 hover:text-blue-600 transition-all duration-300 sm:hidden"
                >
                  {copied ? (
                    <>
                      <CheckCircle className="w-5 h-5 mr-2 text-green-600" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-5 h-5 mr-2" />
                      Copy Link
                    </>
                  )}
                </button>
              </div>
                </div>
                
                {/* Thumbnail Image */}
                {session.thumbnail_url && (
              <motion.div 
                className="flex-shrink-0 w-full sm:w-80 lg:w-96"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl bg-gray-100">
                      <LazyLoadImage 
                        src={session.thumbnail_url} 
                        alt={`${session.name} thumbnail`}
                        className="w-full h-full object-cover"
                        effect="blur"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none'
                        }}
                      />
                    </div>
              </motion.div>
            )}
                  </div>
        </motion.div>

        {/* Tabs Section */}
        <motion.div 
          className="space-y-6"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <div className="flex items-center justify-between">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
              Session Tabs
            </h2>
            <div className="text-sm text-gray-500">
              {session.tabs.length} {session.tabs.length === 1 ? 'tab' : 'tabs'}
                </div>
              </div>
              
          <div className="grid gap-3">
            {tabsToShow?.map((tab, index) => (
              <motion.div
                key={tab.id}
                className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4 hover:border-blue-300 hover:shadow-lg transition-all duration-300 group w-full"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="flex items-start space-x-2 sm:space-x-4">
                  {/* Tab Icon */}
                  <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-gray-50 rounded-lg flex items-center justify-center group-hover:bg-blue-50 transition-colors mt-1">
                        {tab.favicon ? (
                          <img 
                            src={tab.favicon} 
                            alt="" 
                        className="w-4 h-4 sm:w-6 sm:h-6 rounded"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none'
                            }}
                          />
                        ) : (
                      <Globe className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                        )}
                    </div>
                    
                  {/* Tab Info */}
                  <div className="flex-1 min-w-0 space-y-1 overflow-hidden">
                    <h3 
                      className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors leading-tight text-sm sm:text-base"
                      title={tab.title}
                    >
                      {/* Mobile: Show shorter title */}
                      <span className="block sm:hidden">
                        {truncateTitle(tab.title, 25)}
                      </span>
                      {/* Desktop: Show longer title */}
                      <span className="hidden sm:block">
                        {truncateTitle(tab.title, 60)}
                      </span>
                    </h3>
                    <div className="text-xs sm:text-sm text-gray-500">
                      {/* Mobile: Show shorter URL */}
                      <p className="block sm:hidden break-all" title={tab.url}>
                        {truncateUrl(tab.url, 25)}
                      </p>
                      {/* Desktop: Show longer URL */}
                      <p className="hidden sm:block break-all" title={tab.url}>
                        {truncateUrl(tab.url, 70)}
                      </p>
                    </div>
                  </div>
                  
                  {/* Open Button */}
                  <button 
                    onClick={() => window.open(tab.url, '_blank')}
                    className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-gray-50 hover:bg-blue-600 text-gray-400 hover:text-white rounded-lg flex items-center justify-center transition-all duration-300 group-hover:bg-blue-600 group-hover:text-white mt-1"
                    title="Open in new tab"
                  >
                    <ExternalLink className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                </div>
              </motion.div>
            ))}
            
            {/* Show Remaining Button */}
            {remainingCount > 0 && !showAllTabs && (
              <motion.div
                className="flex justify-center mt-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: (tabsToShow?.length || 0) * 0.1 }}
              >
                <button
                  onClick={() => setShowAllTabs(true)}
                  className="inline-flex items-center px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full font-medium transition-all duration-300 shadow-sm hover:shadow-md"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Show {remainingCount} more tab{remainingCount > 1 ? 's' : ''}
                </button>
              </motion.div>
            )}
          </div>
        </motion.div>

        {/* CTA Section */}
        <ReadyToOrganizeCTA />
      </main>

      {/* Results Modal */}
      {showResultsModal && tabResults && (
        <motion.div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setShowResultsModal(false)}
        >
          <motion.div
            className="bg-white rounded-3xl shadow-2xl max-w-md w-full"
            initial={{ scale: 0.95, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 20 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setShowResultsModal(false)}
              className="absolute top-4 right-4 w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors duration-200 z-10"
            >
              <X className="w-4 h-4 text-gray-600" />
            </button>

            {/* Header */}
            <div className="px-6 py-5 text-center border-b border-gray-100">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-3">
                {tabResults.openedCount > 0 ? (
                  <CheckCircle className="w-6 h-6 text-white" />
                ) : (
                  <AlertCircle className="w-6 h-6 text-white" />
                )}
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-1">
                {tabResults.openedCount > 0 ? 'Tabs Opened!' : 'No Tabs Opened'}
              </h3>
              <p className="text-sm text-gray-600">
                {tabResults.openedCount} of {tabResults.totalTabs} tabs were opened successfully
              </p>
            </div>

            {/* Content */}
            <div className="px-6 py-4 space-y-4">
              {/* Success Summary */}
              {tabResults.openedCount > 0 && (
                <div className="flex items-center justify-between bg-green-50 rounded-lg p-3">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-sm font-medium text-green-800">
                      {tabResults.openedCount} tabs opened
                    </span>
                  </div>
                  <CheckCircle className="w-4 h-4 text-green-600" />
                </div>
              )}

              {/* Blocked Summary */}
              {tabResults.blockedCount > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between bg-orange-50 rounded-lg p-3">
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                      <span className="text-sm font-medium text-orange-800">
                        {tabResults.blockedCount} tabs blocked
                      </span>
                    </div>
                    <AlertCircle className="w-4 h-4 text-orange-600" />
                  </div>
                  
                  {/* Blocked List */}
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs font-medium text-gray-700 mb-2">
                      Blocked tabs:
                    </p>
                    <div className="space-y-1">
                      {tabResults.blockedUrls.slice(0, 3).map((title, index) => (
                        <div key={index} className="flex items-center space-x-2 text-xs">
                          <div className="w-1 h-1 bg-orange-400 rounded-full flex-shrink-0"></div>
                          <span className="text-gray-700 truncate">{title}</span>
                        </div>
                      ))}
                      {tabResults.blockedUrls.length > 3 && (
                        <div className="text-xs text-gray-500 mt-1">
                          +{tabResults.blockedUrls.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* FAQ Style Accordion */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setShowFaq(!showFaq)}
                  className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex items-center justify-between transition-colors duration-200"
                >
                  <div className="flex items-center space-x-2">
                    <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-600 text-xs font-bold">?</span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      Why were some tabs blocked?
                    </span>
                  </div>
                  <motion.div
                    animate={{ rotate: showFaq ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ArrowRight className="w-4 h-4 text-gray-500" />
                  </motion.div>
                </button>
                
                <motion.div
                  initial={false}
                  animate={{ height: showFaq ? 'auto' : 0, opacity: showFaq ? 1 : 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="px-4 py-3 bg-white border-t border-gray-200">
                    <p className="text-xs text-gray-600 leading-relaxed">
                      Browser security prevents opening internal pages like "New Tab" and "Extensions" to protect your privacy and security.
                    </p>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100">
              <button
                onClick={() => setShowResultsModal(false)}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2.5 px-4 rounded-lg transition-colors duration-200 text-sm"
              >
                Got it
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  )
}

export default SharePage 